import 'gleam-shared/embeds/GalleryPreview.scss'
import onceDetaches from 'gleam-shared/browser/onceDetaches'
import whenStateChanges from 'gleam-shared/browser/whenStateChanges'
import { writeDom, cancelFastDom } from 'gleam-shared/browser/fastDom'

import DeadPreview from 'gleam-shared/embeds/DeadPreview'
import BasePreview from 'gleam-shared/embeds/BasePreview'

/*
 *           ==== IMPORTANT NOTE ====
 *
 *             Keep it in sync with
 *   app/gallery-v2/src/utils/shared-constants.js
 *  app/gallery-v2/src/utils/shared-constants.less
 *
 */

const READY_ATTR = 'data-ready'
const OVERLAY_CLASS = 'ggs-bootstrap-overlay'
export const GALLERY_EMBED_ATTR = 'data-gallery-embed'
export const GALLERY_INCLUDE_ATTR = 'data-gallery-include'

/*             ==== THANK YOU! ====           */

const CONTAINER_CLASS = 'gallery-preview__container'
const RICH_PLACEHOLDER_CLASS = 'gallery-preview__rich-placeholder'
const RICH_GRID_PLACEHOLDER_CLASS = 'gallery-preview__rich-grid-placeholder'
const RICH_TILE_PLACEHOLDER_CLASS = 'gallery-preview__rich-tile-placeholder'
const RICH_TITLE_PLACEHOLDER_CLASS = 'gallery-preview__rich-title-placeholder'
const RICH_PACEHOLDER_RADIUS_CLASS = 'gallery-preview__rich-placeholder-radius'

export function injectPlaceholder(container) {
  preconfigure(container)
}

export default function GalleryPreview(container) {
  try {
    const key = getKey(container)

    return RealPreview(container, key)
  } catch (err) {
    return DeadPreview('The gallery preview', err)
  }
}

function RealPreview(container, key) {
  const preview = BasePreview({
    destroy,
    description: `The ${key} gallery`
  })

  let embed = null
  const detachObserver = onceDetaches(container, destroy)

  writeDom(configure)

  return preview

  function configure() {
    preconfigure(container)

    whenStateChanges(container, { loaded: READY_ATTR }).then(
      preview.loadEmitter.emit,
      preview.errorEmitter.emit
    )

    if (isEmbed(container)) {
      importEmbedLauncher().then((launchEmbedGallery) => {
        embed = launchEmbedGallery(container)
      }).catch(preview.errorEmitter.emit)
    } else {
      importIncludeLauncher().then((launchIncludeGallery) => {
        launchIncludeGallery(container)
      }).catch(preview.errorEmitter.emit)
    }
  }

  function destroy() {
    cancelFastDom(configure)
    detachObserver.destroy()
    embed?.destroy()
  }
}

function preconfigure(container) {
  if (isEmbed(container)) {
    configureEmbedContainer(container)
    injectPlainOverlay(container)
  } else {
    configureIncludeContainer(container)
    injectRichPlaceholder(container)
  }
}

function configureEmbedContainer(container) {
  container.setAttribute('data-strict-order', 'true')
  container.setAttribute('data-force-hosted-share-link', 'true')
  container.classList.add(CONTAINER_CLASS)
}

function configureIncludeContainer(container) {
  container.setAttribute('data-strict-order', 'true')
  container.setAttribute('data-set-min-height', 'false')
  container.setAttribute('data-use-bootstrap-overlay', 'false')
  container.setAttribute('data-requires-css-hard-reset', 'true')
  container.setAttribute('data-force-hosted-share-link', 'true')
  container.setAttribute('data-allow-overflow-y-change', 'false')
  container.setAttribute('data-instant-mode-until-ready', 'true')
  container.classList.add(CONTAINER_CLASS)
}

function injectPlainOverlay(container) {
  if (findOverlay(container)) {
    return
  }

  const overlay = buildSection(OVERLAY_CLASS)
  container.append(overlay)
}

function injectRichPlaceholder(container) {
  injectPlainOverlay(container)
  const overlay = findOverlay(container)

  if (overlay.classList.contains(RICH_PLACEHOLDER_CLASS)) {
    return
  }

  overlay.classList.add(RICH_PLACEHOLDER_CLASS)

  const grid = buildSection(RICH_GRID_PLACEHOLDER_CLASS)
  const cover = buildSection(RICH_TITLE_PLACEHOLDER_CLASS)
  const radius = buildSection(RICH_PACEHOLDER_RADIUS_CLASS)

  for (let index = 0; index < 100; index++) {
    grid.append(buildSection(RICH_TILE_PLACEHOLDER_CLASS))
  }

  overlay.append(radius)
  radius.append(cover)
  radius.append(grid)
}

function findOverlay(container) {
  return container.getElementsByClassName(OVERLAY_CLASS)[0]
}

function buildSection(className) {
  const section = document.createElement('section')
  section.classList.add(className)
  return section
}

function getKey(container) {
  const key = container.getAttribute(GALLERY_EMBED_ATTR) || container.getAttribute(GALLERY_INCLUDE_ATTR)

  if (!key) {
    throw new Error('The key is missing!')
  }

  return key
}

function isEmbed(container) {
  return container.hasAttribute(GALLERY_EMBED_ATTR)
}

function importEmbedLauncher() {
  return import(
    /* webpackChunkName: "gleam-shared.gallery-preview.embed" */
    'gallery-v2/launcher/launch.embed-gallery'
  ).then((module) => {
    return module.default
  })
}

function importIncludeLauncher() {
  return import(
    /* webpackChunkName: "gleam-shared.gallery-preview.include" */
     'gallery-v2/launcher/launch.include-gallery'
  ).then((module) => {
    return module.default
  })
}
